import BrandMark from './BrandMark'
import type { PhoneLabels } from '@/pages/landing/types'

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mx-auto w-[260px] sm:w-[280px]">
      <div className="rounded-[2.5rem] bg-ink p-2.5 shadow-2xl shadow-ink/20">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-ink rounded-b-2xl z-10" />
        <div className="rounded-[2rem] bg-paper overflow-hidden min-h-[420px]">
          <div className="px-5 pt-8 pb-2 flex items-center justify-between text-[10px] text-ink-faint">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <span>●●●</span>
              <span>🔋</span>
            </div>
          </div>
          <div className="flex flex-col items-center pb-2">
            <BrandMark className="w-7 h-8" />
            <span className="font-body font-extrabold text-sm text-ink">Kego Fûté</span>
          </div>
          <div className="px-4 pb-5">{children}</div>
        </div>
      </div>
    </div>
  )
}

function InputMock({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2.5">
      <div className="text-[9px] font-semibold text-ink-faint mb-1">{label}</div>
      <div className="bg-card border-2 border-ink rounded-lg px-3 py-2 text-[11px] text-ink">{value}</div>
    </div>
  )
}

function SignupScreen({ labels }: { labels: PhoneLabels }) {
  return (
    <div className="rounded-xl bg-card border border-line shadow-sm p-4">
      <div className="text-xs font-extrabold text-ink mb-3">{labels.signup_title}</div>
      <InputMock label={labels.signup_email_label} value={labels.signup_email} />
      <InputMock label={labels.signup_pw_label} value="••••••••" />
      <button className="w-full rounded-lg bg-flame text-paper text-xs font-bold py-2.5 border-2 border-ink shadow-[3px_3px_0_#2A1608]">
        {labels.signup_cta}
      </button>
      <p className="text-[9px] text-ink-faint text-center mt-2">{labels.signup_note}</p>
    </div>
  )
}

function LabelScreen({ labels }: { labels: PhoneLabels }) {
  return (
    <div className="rounded-xl bg-card border border-line shadow-sm p-4 text-center">
      <div className="text-xs font-extrabold text-ink mb-3">{labels.label_title}</div>
      <svg viewBox="0 0 100 100" className="w-24 h-24 mx-auto mb-3" aria-hidden="true">
        <rect width="100" height="100" fill="#FFFDF7" />
        <rect x="6" y="6" width="24" height="24" fill="none" stroke="#2A1608" strokeWidth="6" />
        <rect x="14" y="14" width="8" height="8" fill="#2A1608" />
        <rect x="70" y="6" width="24" height="24" fill="none" stroke="#2A1608" strokeWidth="6" />
        <rect x="78" y="14" width="8" height="8" fill="#2A1608" />
        <rect x="6" y="70" width="24" height="24" fill="none" stroke="#2A1608" strokeWidth="6" />
        <rect x="14" y="78" width="8" height="8" fill="#2A1608" />
        <rect x="42" y="8" width="6" height="6" fill="#2A1608" /><rect x="54" y="8" width="6" height="6" fill="#2A1608" />
        <rect x="40" y="40" width="6" height="6" fill="#2A1608" /><rect x="52" y="46" width="6" height="6" fill="#2A1608" />
        <rect x="64" y="40" width="6" height="6" fill="#2A1608" /><rect x="40" y="58" width="6" height="6" fill="#2A1608" />
        <rect x="8" y="42" width="6" height="6" fill="#2A1608" /><rect x="8" y="54" width="6" height="6" fill="#2A1608" />
        <rect x="88" y="42" width="6" height="6" fill="#2A1608" /><rect x="76" y="54" width="6" height="6" fill="#2A1608" />
        <rect x="64" y="64" width="6" height="6" fill="#2A1608" /><rect x="76" y="76" width="6" height="6" fill="#2A1608" />
        <rect x="88" y="66" width="6" height="6" fill="#2A1608" /><rect x="52" y="76" width="6" height="6" fill="#2A1608" />
        <rect x="42" y="88" width="6" height="6" fill="#2A1608" /><rect x="64" y="88" width="6" height="6" fill="#2A1608" />
      </svg>
      <button className="w-full rounded-lg bg-leaf text-paper text-xs font-bold py-2.5 border-2 border-ink shadow-[3px_3px_0_#2A1608]">
        {labels.label_cta}
      </button>
      <p className="text-[9px] text-ink-faint mt-2">{labels.label_note}</p>
    </div>
  )
}

const dotClass: Record<string, string> = { mustard: 'bg-mustard', flame: 'bg-flame', leaf: 'bg-leaf' }

function TrackScreen({ labels }: { labels: PhoneLabels }) {
  return (
    <div className="rounded-xl bg-card border border-line shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-extrabold text-ink">{labels.track_title}</span>
        <span className="text-[9px] font-bold text-flame">{labels.track_live}</span>
      </div>
      <div className="space-y-2">
        {labels.track_rows.map((row) => (
          <div key={row.id} className="flex items-center justify-between border border-line rounded-lg px-2.5 py-2">
            <span className="text-[11px] font-semibold text-ink">{row.id}</span>
            <span className="inline-flex items-center gap-1 text-[10px] text-ink-soft">
              <span className={`w-1.5 h-1.5 rounded-full ${dotClass[row.dot]}`} />
              {row.where}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[10px] font-bold text-leaf-deep">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
        {labels.track_footer}
      </div>
    </div>
  )
}

export default function MobileScanMockup({ step = 0, labels }: { step?: number; labels: PhoneLabels }) {
  return (
    <PhoneFrame>
      {step === 0 && <SignupScreen labels={labels} />}
      {step === 1 && <LabelScreen labels={labels} />}
      {step === 2 && <TrackScreen labels={labels} />}
    </PhoneFrame>
  )
}
